<template>
  <div class="flex gap-2">
    <CheckboxComponent class="mt-1" v-model="isEnabled" />
    <div class="flex flex-col gap-2">
      <span class="font-medium">{{ props.label }}</span>
      <span class="text-grey-light-600 text-sm">
        {{ props.subText }}
      </span>
      <span class="text-warning-600" v-if="sectionKey === 'expiredOrder'">
        This notification is not recommended for e-commerce integrations.
      </span>
    </div>
    <Button
      v-if="isEnabled"
      @click="showEmails = !showEmails"
      class="self-center ml-auto"
      variant="outline"
      size="sm"
      type="button"
    >
      <ChevronsUpDown class="h-4 w-4" />
      Show / Hide emails
    </Button>
  </div>

  <div class="grid grid-cols-5" v-if="isEnabled && props.sectionKey === 'completedOrder_75'">
    <div class="col-span-1">
      <Select v-model="percentage">
        <SelectTrigger>
          <SelectValue placeholder="%" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem v-for="num in 100" :key="num" :value="num.toString()">{{ num }}</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  </div>

  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 max-h-0 overflow-hidden"
    enter-to-class="opacity-100 max-h-96 overflow-visible"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="opacity-100 max-h-96 overflow-visible"
    leave-to-class="opacity-0 max-h-0 overflow-hidden"
  >
    <div v-if="showEmails" class="space-y-2">
      <SectionDivider class="my-0" />
      <div class="flex">
        <Input v-model="accountEmail" placeholder="john@doe.com" type="email" class="rounded-r-none" />
        <Button @click="addEmail" variant="outline" type="button" class="rounded-l-none border-l-0 shadow-none">
          <Plus class="h-4 w-4" />
          Add
        </Button>
      </div>
      <p v-if="error !== ''" class="text-sm font-medium text-destructive">
        {{ error }}
      </p>
      <ul v-if="emails.length" class="space-y-2">
        <li v-for="email in emails" :key="email">
          <div class="flex">
            <span
              class="h-10 w-full rounded-lg rounded-r-none border border-input bg-background px-3 py-2 text-gray-900"
            >
              {{ email }}
            </span>
            <Button
              @click="removeEmail(email)"
              variant="outline"
              type="button"
              class="rounded-l-none border-l-0 shadow-none"
            >
              <Trash2 class="h-4 w-4" />
              Remove
            </Button>
          </div>
        </li>
      </ul>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { NotificationSetting } from '@/models/settings';
import { ChevronsUpDown, Plus, Trash2 } from 'lucide-vue-next';
import { ref, watch } from 'vue';

const props = defineProps<{
  section: NotificationSetting;
  sectionKey: string;
  label: string;
  subText: string;
}>();

const emits = defineEmits(['update:section']);

const isEnabled = ref(false);
const showEmails = ref(false);
const percentage = ref('');
const accountEmail = ref('');
const error = ref('');
const emails = ref<string[]>([]);

watch(
  () => props.section,
  (newSection) => {
    isEnabled.value = newSection.enabled;
    emails.value = newSection.emails;
    if (props.sectionKey === 'completedOrder_75') {
      percentage.value = newSection.value?.toString() || '0';
    }
  },
  { immediate: true },
);

watch(
  () => isEnabled.value,
  () => handleChange(),
);

watch(
  () => percentage.value,
  () => handleChange(),
);

const addEmail = () => {
  const email = accountEmail.value.trim();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailPattern.test(email)) {
    error.value = 'Please enter a valid email address.';
    return;
  }

  if (emails.value.includes(email)) {
    error.value = 'Email already added.';
    return;
  }

  emails.value.push(email);
  accountEmail.value = '';
  error.value = '';

  handleChange();
};

const removeEmail = (email: string) => {
  emails.value = emails.value.filter((e) => e !== email);
  handleChange();
};

const handleChange = () => {
  const section: NotificationSetting = {
    enabled: isEnabled.value,
    emails: emails.value,
  };

  if (props.sectionKey === 'completedOrder_75') {
    const percentageValue = parseFloat(percentage.value);
    section.value = !isNaN(percentageValue) ? percentageValue : 0;
  }

  emits('update:section', { section, sectionKey: props.sectionKey });
};
</script>
